import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, useNavigate, useLocation, useParams } from "react-router-dom";
import Menu from "./components/Menu";
import ChatList from "./components/ChatList";
import { useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import NotificationCenter from "./components/NotificationCenter";
import ChatWindow from "./components/ChatWindow";
import SchedulePage from "./components/SchedulePage";
import EditSchedulePage from "./components/EditSchedulePage";
import ScheduledListPage from "./components/ScheduledListPage";
import AIAssistantForm from "./components/AIAssistantForm";
import AIListPage from "./components/AIListPage";
import EditIAPage from "./components/EditIAPage";
import { useParentMessage } from "./hooks/useParentMessage";
import Login from "./components/Login";
import { getTomadoresContact } from "./services/Contacts";
import { MdGroup } from "react-icons/md";
import { useNotification } from "./context/NotificationContext";
import { getChats } from "./services/chat";
import { getProfile } from "./services/profile";
import { useSocket } from "./context/SocketContext";
import { getTomadorByStamp } from "./services/Contacts";
import TicketList from "./components/TicketList";


function GlobalNotificationListener({ setConversations }) {
  const socket = useSocket();
  const { token } = useAuth();
  const { addNotification } = useNotification();
  const { notifyParent } = useParentMessage();

  useEffect(() => {
    if (!socket || !token) return;
	


    const handleNewMessage = async (data) => {
      if (!data) return;

      const senderIds = [
        data.UTSTP,
        data.USER,
        data.user,
        data.USERSTP,
        data.utilizadorStamp,
      ]
        .filter(Boolean)
        .map(String);
      const isOwnMessage =
        String(data.nomeUtilizador || "").trim().toUpperCase() === "EU" ||
        senderIds.includes(String(token));

      if (isOwnMessage) return;

      const preview = (data.CONTEUDO || data.mensagem || "Mensagem recebida")
        .substring(0, 40);
      const sender = data.nomeUtilizador || "Novo utilizador";

      try {
        const chatData = await getChats();
        setConversations(chatData.chats);

        const incomingId = data?.PUBLIC_ID || data?.CHATSTP || data?.STAMP;
        if (!incomingId) return;

        const chat = chatData.chats.find(
          (conversation) =>
            conversation.PUBLIC_ID === incomingId ||
            conversation.STAMP === incomingId
        );

        if (!chat) return;

       const dataProfile = await getProfile();

          if(dataProfile.NOME != data.nomeUtilizador){
                 
                notifyParent({
                  chatStamp: chat.PUBLIC_ID,
                  message: preview,
                  sender,
                });
         


               addNotification({
                sender: data.nomeUtilizador || chat.NOME || sender,
                message: preview,
                avatar: chat.LOGO || data.FOTO,
                type: "message",
                chatStamp: chat.PUBLIC_ID,
              });
          }
         
         

      } catch (error) {
        console.error(error);
      }
    };

    socket.on("nova_mensagemLista", handleNewMessage);

    return () => {
      socket.off("nova_mensagemLista", handleNewMessage);
    };
  }, [socket, token, addNotification, setConversations]);

  return null;
}



function App() {

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSubmittingAI, setIsSubmittingAI] = useState(false);
  const location = useLocation();
  const { id: publicId } = useParams();

  const { token, stampTomador } = useAuth();
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedAgendamento, setSelectedAgendamento] = useState(null);
  const [selectedIA, setSelectedIA] = useState(null);

  const [menuOpen, setMenuOpen] = useState(true);
  const [pendingTomador, setPendingTomador] = useState(null);
  const [chatsLoaded, setChatsLoaded] = useState(false);

  const [conversations, setConversations] = useState([]);

  const navigate = useNavigate();

  const selectedConversationId =selectedConversation?.PUBLIC_ID ?? selectedConversation?.STAMP;

  const selectedConversationStamp =selectedConversation?.STAMP; 

   const initializedTomadorStamp = useRef(null);

  const didInitTomador = useRef(false);

  useParentMessage();

  useEffect(() => {
    if (!token) {
      setConversations([]);
      setChatsLoaded(false);
      return;
    }

    let active = true;

    const loadConversations = async () => {
      try {
        const data = await getChats(20, null, "");
        if (!active) return;

        setConversations(Array.isArray(data?.chats) ? data.chats : []);
        setChatsLoaded(true);
      } catch (err) {
        console.error("Erro ao carregar conversas:", err);
        if (active) {
          setConversations([]);
          setChatsLoaded(true);
        }
      }
    };

    setChatsLoaded(false);
    loadConversations();

    return () => {
      active = false;
    };
  }, [token]);


  const refreshChats = async (lastMessageOverride = null, lastConversationId = null) => {
    try {
      const data = await getChats(20, null, "", true);
      const refreshed = Array.isArray(data?.chats) ? data.chats : [];

      const currentMap = new Map(
        conversations.map((chat) => [String(chat.PUBLIC_ID || chat.STAMP || ""), chat])
      );

      const targetId = String(lastConversationId || selectedConversation?.PUBLIC_ID || selectedConversation?.STAMP || "");

      const merged = refreshed.map((chat) => {
        const chatKey = String(chat.PUBLIC_ID || chat.STAMP || "");
        const previous = currentMap.get(chatKey) || {};
        const hasExplicitOverride = Boolean(lastMessageOverride && targetId && chatKey === targetId);

        return {
          ...previous,
          ...chat,
          ULTMSG: hasExplicitOverride
            ? lastMessageOverride
            : (chat.ULTMSG ?? previous.ULTMSG ?? ""),
          DATAENV: hasExplicitOverride
            ? new Date().toISOString()
            : (chat.DATAENV ?? previous.DATAENV ?? new Date().toISOString()),
        };
      });

      if (lastMessageOverride && targetId) {
        const exists = merged.some(
          (chat) => String(chat.PUBLIC_ID || chat.STAMP || "") === targetId
        );

        if (!exists && selectedConversation) {
          merged.unshift({
            ...selectedConversation,
            ULTMSG: lastMessageOverride,
            DATAENV: new Date().toISOString(),
          });
        }


      }

      setConversations(merged);
    } catch (err) {
      console.error("Erro ao recarregar chats:", err);
    }
  };



  const emptyChatStyle = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f8fafc",
  };

  const emptyChatContent = {
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 20,
  };

  const handleLogout = () => {
    setSelectedConversation(null);
    setPendingTomador(null);
    setConversations([]);
    didInitTomador.current = false;

    navigate("/login");
  };

  const handleSelectConversation = (conversation) => {

    setSelectedConversation(conversation);

    navigate(`/conversas/${conversation.PUBLIC_ID}`);

    if (isMobile) {
      setMenuOpen(false);
    }
  };

  const back = () => {
    setSelectedConversation(null);

    navigate("/conversas");

    if (isMobile) {
      setMenuOpen(true);
    }
  };

  useEffect(() => {
    if (!stampTomador || !chatsLoaded) return;

    const currentStamp = String(stampTomador);

    if (initializedTomadorStamp.current === currentStamp) return;

    initializedTomadorStamp.current = currentStamp;

    const existing = conversations.find(
      (c) =>
        String(c.TMSTP || c.STAMPTOMADOR || c.STAMP || c.PUBLIC_ID || "") === currentStamp
    );

    if (existing) {
      setPendingTomador(null);
      setSelectedConversation(existing);
      navigate(`/conversas/${existing.PUBLIC_ID || existing.STAMP}`);
      return;
    }

    const load = async () => {
      try {
        const data = await getTomadorByStamp(currentStamp);
        const tomador = data?.STAMP ? data : null;

        if (!tomador) {
          setPendingTomador(null);
          return;
        }

        setSelectedConversation({
          STAMP: `TEMP_PRIVATE_${currentStamp}`,
          NOME: tomador.NOME,
          IS_TEMP: true,
          FOTO: tomador.FOTO,
          STAMPTOMADOR: currentStamp,
        });

        navigate("/conversas");
      } catch (err) {
        setPendingTomador(null);
        console.error("Erro ao buscar tomadores:", err);
      }
    };

    load();
  }, [stampTomador, chatsLoaded, conversations, token, navigate]);

  

  useEffect(() => {

    
      if (!publicId || conversations.length === 0) return;

      const chat = conversations.find(
        c => c.PUBLIC_ID === publicId || c.STAMP === publicId
      );

      if (chat) {
        setSelectedConversation(chat);
      }
  }, [publicId, conversations]);


  useEffect(() => {
      if (!conversations || conversations.length === 0) return;
      if (selectedConversation) return;
      if (initialLoaded) return;

      const firstChat = conversations[0];

      setSelectedConversation(firstChat);
      navigate(`/conversas/${firstChat.PUBLIC_ID}`);

      setInitialLoaded(true);
  }, [conversations]);

  useEffect(() => {

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };

  }, []);

  useEffect(() => {
    if (!token && location.pathname !== "/login") {
      navigate("/login");
    }

    if (token && location.pathname === "/login") {
      navigate("/conversas");
    }
  }, [token, location.pathname, navigate]);

  if (!token) {
    return <Login />;
  }

  const handleAIAssistantSubmit = async (data) => {

    setIsSubmittingAI(true);

    try {
      navigate("/ais");

    } catch (error) {

      console.error("Erro ao criar assistente:", error);

    } finally {

      setIsSubmittingAI(false);
    }
  };

  const handleNotificationClick = (notification) => {

    if (!notification.chatStamp) return;

    const chat = conversations.find(
      (c) => c.STAMP === notification.chatStamp
    );

    if (!chat) return;

    setSelectedConversation(chat);

    navigate(`/conversas/${chat.PUBLIC_ID}`);
  };

  return (
  <NotificationProvider>
    <div
      style={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <NotificationCenter onNotificationClick={handleNotificationClick} />
      <GlobalNotificationListener
        setConversations={setConversations}
      />

      <Menu isMobile={isMobile} onLogout={handleLogout} />

      <Routes>
        {/* ================= HOME ================= */}
        <Route
          path="/"
          element={
            <div style={emptyChatStyle}>
              <div style={emptyChatContent}>
                <MdGroup size={70} color="#cbd5e1" />
                <h2>Selecciona uma conversa</h2>
                <p style={{ color: "#94a3b8" }}>
                  Escolhe um chat da lista
                </p>
              </div>
            </div>
          }
        />

        {/* ================= CHAT LAYOUT (LISTA + WINDOW) ================= */}
        <Route
          path="/conversas/*"
          element={
            <div style={{ display: "flex", flex: 1, minWidth: 0 }}>
              <ChatList
                conversations={conversations}
                setConversations={setConversations}
                onSelectConversation={handleSelectConversation}
                isMobile={isMobile}
                menuOpen={menuOpen}
                selectedConversationId={selectedConversationId}
                selectedConversationStamp={selectedConversationStamp}
              />

              {selectedConversation ? (
                <ChatWindow
                  conversationData={selectedConversation}
                  setConversationData={setSelectedConversation}
                  isMobile={isMobile}
                  pendingTomador={pendingTomador}
                  onBack={back}
                  setSelectedConversation={setSelectedConversation}
                   refreshChats={refreshChats}
                />
              ) : (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f8fafc",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <MdGroup size={70} color="#cbd5e1" />
                    <h2>Selecciona uma conversa</h2>
                    <p style={{ color: "#94a3b8" }}>
                      Escolhe um chat da lista
                    </p>
                  </div>
                </div>
              )}
            </div>
          }
        />

        {/* ================= AGENDAMENTOS ================= */}
        <Route
          path="/agendar"
          element={
            <ScheduledListPage setSelectedAgendamento={setSelectedAgendamento} />
          }
        />

        <Route path="/agendar/novo" element={<SchedulePage />} />

        <Route
          path="/agendar/editar"
          element={<EditSchedulePage agendamento={selectedAgendamento} />}
        />

        {/* ================= AIS ================= */}
        <Route
          path="/ais"
          element={<AIListPage setSelectedIA={setSelectedIA} isMobile={isMobile} />}
        />

        <Route
          path="/ais/criar"
          element={
            <AIAssistantForm
              onSubmit={handleAIAssistantSubmit}
              onCancel={() => navigate("/ais")}
              isLoading={isSubmittingAI}
              isMobile={isMobile}
            />
          }
        />

        <Route
          path="/ais/editar"
          element={<EditIAPage ia={selectedIA} />}
        />
		
		{/* ================= TICKETS ================= */}
		<Route
          path="/tickets"
          element={<TicketList isMobile={isMobile} />}
        />
		
		
      </Routes>
    </div>
  </NotificationProvider>
);
}

export default App;

